import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PublicNav } from "@/components/PublicNav";
import { PublicFooter } from "@/components/PublicFooter";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy — VISIKT" },
      {
        name: "description",
        content:
          "How VIKEEP AS collects and processes personal data in VISIKT, in accordance with the GDPR and the Norwegian Personal Data Act.",
      },
      { property: "og:title", content: "Privacy Policy — VISIKT" },
      {
        property: "og:description",
        content:
          "How VIKEEP AS collects and processes personal data in VISIKT, under the GDPR and Norwegian law.",
      },
      { property: "og:url", content: "https://visikt.com/privacy" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://visikt.com/privacy" }],
  }),
  component: PrivacyPage,
});

type Section = {
  title: string;
  paragraphs?: Array<string | Array<{ text: string; italic?: boolean; bold?: boolean }>>;
  table?: { header: [string, string]; rows: Array<[string, string]> };
};

type PolicyContent = {
  intro: string;
  sections: Section[];
};

const CONTENT_EN: PolicyContent = {
  intro:
    'This privacy policy explains how VIKEEP AS ("VIKEEP", "we", "us", "our") collects and processes personal data when you use VISIKT (the "Service") — our app for tracking subscriptions, salary, and inflation-adjusted purchasing power. We process personal data in accordance with the EU General Data Protection Regulation (Regulation (EU) 2016/679, "GDPR") as incorporated into Norwegian law through the Personal Data Act (personopplysningsloven).',
  sections: [
    {
      title: "1. Data controller.",
      paragraphs: [
        "VIKEEP AS is the data controller for your personal data in VISIKT. Company: VIKEEP AS. Organisation number: 938 008 272. Address: Oslo, Norway. Email: contact@vikeep.no. Contact us with any questions about this policy or our processing.",
      ],
    },
    {
      title: "2. What personal data we collect.",
      paragraphs: [
        [
          { text: "a) Information you give us at registration and in use: ", italic: true },
          {
            text: "first and last name; email; postal code, city and country; phone number; occupation; password (stored only in securely hashed form — never in plain text); language preference.",
          },
        ],
        [
          { text: "b) Financial information you choose to add: ", italic: true },
          {
            text: "subscriptions you track (name, category, cost, billing cycle, start date, notes); salary entries you add (gross annual salary, bonuses, effective dates, notes); your selected plan (Free or Pro) and related settings. This is information you choose to enter so VISIKT can show your costs, salary growth and real purchasing power. We treat it as confidential.",
          },
        ],
        [
          { text: "c) Payment information (Pro plans): ", italic: true },
          {
            text: "payment is handled by Stripe. Card details are entered directly with Stripe and are not stored on our servers. We receive limited billing information (subscription status, plan, billing period, payment outcomes) so we can grant access to paid features.",
          },
        ],
        [
          { text: "d) Technical and usage data: ", italic: true },
          {
            text: "account identifiers and authentication tokens (including, if you sign in with Google, the basic account information Google returns); limited technical/log data needed to operate the Service securely and detect errors and abuse.",
          },
        ],
        [
          { text: "e) Support and contact data: ", italic: true },
          {
            text: "if you contact us — including via the in-app contact form shown if registration or sign-in fails — we process what you provide to help you.",
          },
        ],
        "We do not intentionally collect special categories of personal data (such as health, religion, or political opinions); please do not enter such data in free-text fields.",
      ],
    },
    {
      title: "3. Why we process your data and our legal basis.",
      table: {
        header: ["Purpose", "Legal basis (GDPR Article 6)"],
        rows: [
          [
            "Creating and managing your account; providing core features; storing the subscriptions and salary data you enter",
            "Performance of a contract — Art. 6(1)(b)",
          ],
          [
            "Processing payments and managing paid subscriptions",
            "Performance of a contract — Art. 6(1)(b)",
          ],
          [
            "Keeping accounting and invoicing records",
            "Legal obligation — Art. 6(1)(c) (Bookkeeping Act, bokføringsloven)",
          ],
          [
            "Securing the Service, preventing fraud/abuse, improving reliability",
            "Legitimate interests — Art. 6(1)(f)",
          ],
          [
            "Responding to support requests",
            "Contract and/or legitimate interests — Art. 6(1)(b)/(f)",
          ],
          ["Optional cookies/analytics and any marketing", "Consent — Art. 6(1)(a)"],
        ],
      },
      paragraphs: [
        "Where we rely on consent, you may withdraw it at any time (this does not affect prior processing). Where we rely on legitimate interests, we have assessed that our interest in a secure, reliable service does not override your rights; you can object (see Section 8).",
      ],
    },
    {
      title: "4. Who we share your data with.",
      paragraphs: [
        "We do not sell your personal data and never sell or rent the financial information you enter. We share data only with parties that help us run the Service, acting as our data processors under data processing agreements: hosting/database on Supabase / Lovable Cloud within the EU/EEA, protected with row-level security so each account accesses only its own data; payments via Stripe (https://stripe.com/privacy); sign-in with Google, if you choose it (https://policies.google.com/privacy). We may also disclose data where legally required (e.g. to tax authorities or a lawful request), or to establish, exercise or defend legal claims.",
      ],
    },
    {
      title: "5. Where your data is stored and transfers outside the EEA.",
      paragraphs: [
        "We aim to store and process personal data within the EU/EEA. Where a processor operates outside the EU/EEA, we ensure adequate protection via a GDPR-recognised mechanism (an EU Commission adequacy decision, or Standard Contractual Clauses plus any additional safeguards). Accounting records we are legally required to keep are stored within the EEA in line with the Bookkeeping Act.",
      ],
    },
    {
      title: "6. How long we keep your data.",
      paragraphs: [
        "Only as long as necessary.",
        [
          { text: "Account/profile and the subscriptions/salary data you enter: ", italic: true },
          {
            text: "kept while your account is active; if you delete your account, deleted or anonymised without undue delay, except where we must keep certain information (below) or need it to defend a legal claim.",
          },
        ],
        [
          { text: "Accounting and payment records: ", italic: true },
          {
            text: "retained as Norwegian law requires — under the Bookkeeping Act, primary accounting documentation (such as invoices and payment records) must generally be kept for five years after the end of the relevant financial year; we cannot delete this earlier even on request.",
          },
        ],
        [
          { text: "Support correspondence: ", italic: true },
          { text: "kept as long as needed to handle your request and a reasonable period after." },
        ],
        [
          { text: "Backups: ", italic: true },
          {
            text: "data may persist in encrypted backups for a limited period after deletion, then is overwritten.",
          },
        ],
      ],
    },
    {
      title: "7. How we protect your data.",
      paragraphs: [
        "Appropriate technical and organisational measures, including: encryption in transit (HTTPS/TLS); row-level security; securely hashed passwords (we never see or store your password in plain text); card data handled entirely by Stripe so card numbers never reach our servers; access controls. No method is completely secure, but we work to protect your data and to detect and respond to incidents. If a personal data breach is likely to result in a high risk to your rights, we will notify you and the Norwegian Data Protection Authority (Datatilsynet) as required.",
      ],
    },
    {
      title: "8. Your rights.",
      paragraphs: [
        'Under the GDPR you have the rights to: be informed; access; rectification; erasure ("right to be forgotten"); restriction of processing; data portability; object (including to direct marketing at any time); and not to be subject to solely automated decisions with legal or similarly significant effects (we do not make such decisions). You can edit much of your profile and your entries directly in the app. To exercise any right, contact contact@vikeep.no; we respond without undue delay and within one month, extendable by up to two further months for complex or numerous requests (we will inform you). Exercising rights is free unless a request is manifestly unfounded or excessive. We may verify your identity before acting.',
      ],
    },
    {
      title: "9. Right to complain.",
      paragraphs: [
        "Please contact us first so we can try to resolve the matter. You may also complain to the Norwegian Data Protection Authority — Datatilsynet, https://www.datatilsynet.no, Postboks 458 Sentrum, 0105 Oslo, Norway.",
      ],
    },
    {
      title: "10. Cookies and similar technologies.",
      paragraphs: [
        "VISIKT uses cookies and local storage that are strictly necessary to operate the Service (e.g. keeping you signed in, remembering your language). These do not require consent. If we introduce non-essential cookies or analytics, we will ask for your consent first, in line with the Electronic Communications Act (ekomloven), and you can change your choice at any time.",
      ],
    },
    {
      title: "11. Children.",
      paragraphs: [
        "VISIKT is intended for adults and is not directed at children. We do not knowingly collect children's personal data; if you believe a child has provided data, contact us and we will delete it.",
      ],
    },
    {
      title: "12. Changes to this policy.",
      paragraphs: [
        'We may update this policy; on material changes we update the "Last updated" date and, where appropriate, notify you in the app or by email.',
      ],
    },
    {
      title: "13. Contact us.",
      paragraphs: ["VIKEEP AS, Org.nr 938 008 272, Oslo, Norway, contact@vikeep.no."],
    },
  ],
};

const CONTENT_NB: PolicyContent = {
  intro:
    'Denne personvernerklæringen forklarer hvordan VIKEEP AS ("VIKEEP", "vi", "oss", "vår") samler inn og behandler personopplysninger når du bruker VISIKT ("Tjenesten") — vår app for å holde oversikt over abonnementer, lønn og inflasjonsjustert kjøpekraft. Vi behandler personopplysninger i samsvar med EUs personvernforordning (forordning (EU) 2016/679, "GDPR"), slik den er gjennomført i norsk rett gjennom personopplysningsloven.',
  sections: [
    {
      title: "1. Behandlingsansvarlig.",
      paragraphs: [
        "VIKEEP AS er behandlingsansvarlig for dine personopplysninger i VISIKT. Selskap: VIKEEP AS. Organisasjonsnummer: 938 008 272. Adresse: Oslo, Norge. E-post: contact@vikeep.no. Kontakt oss ved spørsmål om erklæringen eller behandlingen.",
      ],
    },
    {
      title: "2. Hvilke personopplysninger vi samler inn.",
      paragraphs: [
        [
          { text: "a) Opplysninger du gir oss ved registrering og bruk: ", italic: true },
          {
            text: "fornavn og etternavn; e-post; postnummer, sted og land; telefonnummer; yrke; passord (lagres kun i sikkert hashet form — aldri i klartekst); språkvalg.",
          },
        ],
        [
          { text: "b) Økonomiske opplysninger du selv velger å legge inn: ", italic: true },
          {
            text: "abonnementer du registrerer (navn, kategori, kostnad, betalingssyklus, startdato, notater); lønnsopplysninger (brutto årslønn, bonus, virkningsdatoer, notater); valgt plan (Gratis eller Pro) og innstillinger. Dette er informasjon du selv velger å legge inn slik at VISIKT kan vise kostnader, lønnsvekst og reell kjøpekraft. Vi behandler den som konfidensiell.",
          },
        ],
        [
          { text: "c) Betalingsopplysninger (Pro-planer): ", italic: true },
          {
            text: "betaling håndteres av Stripe. Kortopplysninger legges inn direkte hos Stripe og lagres ikke på våre servere. Vi mottar begrenset faktureringsinformasjon (abonnementsstatus, plan, faktureringsperiode, betalingsutfall) for å gi tilgang til betalte funksjoner.",
          },
        ],
        [
          { text: "d) Tekniske opplysninger og bruksdata: ", italic: true },
          {
            text: "kontoidentifikatorer og autentiseringstokener (inkludert, ved innlogging med Google, den grunnleggende kontoinformasjonen Google returnerer); begrenset teknisk/loggdata som er nødvendig for sikker drift og for å oppdage feil og misbruk.",
          },
        ],
        [
          { text: "e) Support- og kontaktopplysninger: ", italic: true },
          {
            text: "hvis du kontakter oss — inkludert via kontaktskjemaet i appen som vises dersom registrering eller innlogging mislykkes — behandler vi det du oppgir for å hjelpe deg.",
          },
        ],
        "Vi samler ikke bevisst inn særlige kategorier av personopplysninger (som helse, religion eller politisk oppfatning); ikke legg inn slike opplysninger i fritekstfelt.",
      ],
    },
    {
      title: "3. Hvorfor vi behandler opplysningene og vårt behandlingsgrunnlag.",
      table: {
        header: ["Formål", "Behandlingsgrunnlag (GDPR artikkel 6)"],
        rows: [
          [
            "Opprette og administrere kontoen; levere kjernefunksjoner; lagre abonnements- og lønnsopplysningene du legger inn",
            "Oppfyllelse av avtale — art. 6(1)(b)",
          ],
          [
            "Behandle betalinger og administrere betalte abonnementer",
            "Oppfyllelse av avtale — art. 6(1)(b)",
          ],
          [
            "Oppbevare regnskaps- og faktureringsdokumentasjon",
            "Rettslig forpliktelse — art. 6(1)(c) (bokføringsloven)",
          ],
          [
            "Sikre Tjenesten, forhindre svindel/misbruk, forbedre stabilitet",
            "Berettiget interesse — art. 6(1)(f)",
          ],
          [
            "Svare på supporthenvendelser",
            "Avtale og/eller berettiget interesse — art. 6(1)(b)/(f)",
          ],
          ["Valgfrie informasjonskapsler/analyse og markedsføring", "Samtykke — art. 6(1)(a)"],
        ],
      },
      paragraphs: [
        "Der vi bygger på samtykke, kan du når som helst trekke det tilbake (påvirker ikke tidligere behandling). Der vi bygger på berettiget interesse, har vi vurdert at vår interesse i en sikker og stabil tjeneste ikke går foran dine rettigheter; du kan protestere (se punkt 8).",
      ],
    },
    {
      title: "4. Hvem vi deler opplysningene med.",
      paragraphs: [
        "Vi selger ikke personopplysningene dine og selger eller leier aldri ut de økonomiske opplysningene du legger inn. Vi deler kun med parter som hjelper oss å drifte Tjenesten, som våre databehandlere under databehandleravtaler: hosting/database på Supabase / Lovable Cloud innenfor EU/EØS, beskyttet med radnivåsikkerhet slik at hver konto kun har tilgang til egne opplysninger; betaling via Stripe (https://stripe.com/privacy); innlogging med Google, hvis du velger det (https://policies.google.com/privacy). Vi kan utlevere opplysninger der vi er rettslig forpliktet (f.eks. til skattemyndighetene eller en lovlig forespørsel), eller for å fastsette, gjøre gjeldende eller forsvare rettskrav.",
      ],
    },
    {
      title: "5. Hvor opplysningene lagres og overføring utenfor EØS.",
      paragraphs: [
        "Vi tilstreber lagring og behandling innenfor EU/EØS. Der en databehandler opererer utenfor EU/EØS, sikrer vi tilstrekkelig beskyttelse via en GDPR-anerkjent mekanisme (tilstrekkelighetsbeslutning fra EU-kommisjonen, eller Standard Contractual Clauses med nødvendige tilleggsgarantier). Regnskapsdokumentasjon vi er rettslig forpliktet til å oppbevare, lagres innenfor EØS i samsvar med bokføringsloven.",
      ],
    },
    {
      title: "6. Hvor lenge vi oppbevarer opplysningene.",
      paragraphs: [
        "Kun så lenge det er nødvendig.",
        [
          { text: "Konto/profil og abonnements-/lønnsopplysningene du legger inn: ", italic: true },
          {
            text: "oppbevares mens kontoen er aktiv; ved sletting av konto slettes eller anonymiseres de uten ugrunnet opphold, unntatt der vi må beholde visse opplysninger (under) eller trenger dem for å forsvare et rettskrav.",
          },
        ],
        [
          { text: "Regnskaps- og betalingsdokumentasjon: ", italic: true },
          {
            text: "oppbevares som norsk lov krever — etter bokføringsloven må primær regnskapsdokumentasjon (som fakturaer og betalingsbilag) som hovedregel oppbevares i fem år etter utløpet av regnskapsåret; dette kan ikke slettes tidligere, selv på forespørsel.",
          },
        ],
        [
          { text: "Supporthenvendelser: ", italic: true },
          { text: "så lenge det er nødvendig og en rimelig periode etter." },
        ],
        [
          { text: "Sikkerhetskopier: ", italic: true },
          {
            text: "kan finnes i krypterte sikkerhetskopier en begrenset periode etter sletting, før de overskrives.",
          },
        ],
      ],
    },
    {
      title: "7. Hvordan vi beskytter opplysningene dine.",
      paragraphs: [
        "Egnede tekniske og organisatoriske tiltak, inkludert: kryptering under overføring (HTTPS/TLS); radnivåsikkerhet; sikker hashet lagring av passord (vi ser eller lagrer aldri passordet i klartekst); kortopplysninger håndteres i sin helhet av Stripe slik at kortnumre aldri når våre servere; tilgangskontroller. Ingen metode er fullstendig sikker, men vi arbeider for å beskytte opplysningene og håndtere hendelser. Ved brudd som sannsynligvis medfører høy risiko for dine rettigheter, varsler vi deg og Datatilsynet slik loven krever.",
      ],
    },
    {
      title: "8. Dine rettigheter.",
      paragraphs: [
        'Etter GDPR har du rett til: informasjon; innsyn; retting; sletting ("retten til å bli glemt"); begrensning; dataportabilitet; å protestere (inkludert mot direkte markedsføring når som helst); og til ikke å være gjenstand for utelukkende automatiserte avgjørelser med rettsvirkning eller tilsvarende vesentlig betydning (vi treffer ikke slike). Du kan redigere store deler av profilen og oppføringene dine direkte i appen. For å utøve en rettighet, kontakt contact@vikeep.no; vi svarer uten ugrunnet opphold og innen én måned, med mulig forlengelse på inntil to måneder ved komplekse eller mange forespørsler (vi informerer deg). Det er gratis, med mindre en forespørsel er åpenbart grunnløs eller overdreven. Vi kan bekrefte identiteten din før vi behandler forespørselen.',
      ],
    },
    {
      title: "9. Rett til å klage.",
      paragraphs: [
        "Kontakt oss gjerne først slik at vi kan forsøke å løse saken. Du kan også klage til Datatilsynet — https://www.datatilsynet.no, Postboks 458 Sentrum, 0105 Oslo.",
      ],
    },
    {
      title: "10. Informasjonskapsler (cookies) og lignende teknologi.",
      paragraphs: [
        "VISIKT bruker informasjonskapsler og lokal lagring som er strengt nødvendige for å drifte Tjenesten (f.eks. holde deg innlogget, huske språkvalg). Disse krever ikke samtykke. Innfører vi ikke-nødvendige informasjonskapsler eller analyse, ber vi om samtykke først, i samsvar med ekomloven, og du kan endre valget når som helst.",
      ],
    },
    {
      title: "11. Barn.",
      paragraphs: [
        "VISIKT er ment for voksne og er ikke rettet mot barn. Vi samler ikke bevisst inn barns personopplysninger; mener du et barn har gitt oss opplysninger, kontakt oss, så sletter vi dem.",
      ],
    },
    {
      title: "12. Endringer i denne erklæringen.",
      paragraphs: [
        'Vi kan oppdatere erklæringen; ved vesentlige endringer oppdaterer vi datoen for "Sist oppdatert" og varsler der det er hensiktsmessig i appen eller på e-post.',
      ],
    },
    {
      title: "13. Kontakt oss.",
      paragraphs: ["VIKEEP AS, Org.nr 938 008 272, Oslo, Norge, contact@vikeep.no."],
    },
  ],
};

function Paragraph({
  content,
}: {
  content: string | Array<{ text: string; italic?: boolean; bold?: boolean }>;
}) {
  if (typeof content === "string") {
    return <p className="text-sm text-muted-foreground leading-relaxed">{content}</p>;
  }
  return (
    <p className="text-sm text-muted-foreground leading-relaxed">
      {content.map((part, i) => (
        <span
          key={i}
          className={
            (part.italic ? "italic " : "") + (part.bold ? "font-semibold text-foreground " : "")
          }
        >
          {part.text}
        </span>
      ))}
    </p>
  );
}

function PrivacyPage() {
  const { t, i18n } = useTranslation();
  const content = i18n.language?.startsWith("nb") ? CONTENT_NB : CONTENT_EN;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="max-w-2xl mx-auto w-full px-5 pt-8 flex items-center justify-between">
        <Link to="/" className="font-display font-extrabold tracking-tighter text-xl">
          VISIKT
        </Link>
        <PublicNav />
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full px-5 pt-14 pb-16 space-y-10">
        <section className="animate-entry">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-4">
            {t("privacy.eyebrow")}
          </p>
          <h1 className="text-4xl font-display font-bold tracking-tight leading-[1.05]">
            {t("privacy.headline")}
          </h1>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            {t("privacy.lastUpdated")}
          </p>
          <p className="mt-5 text-sm text-muted-foreground leading-relaxed">{content.intro}</p>
        </section>

        {content.sections.map((section, si) => (
          <section key={si} className="animate-entry space-y-3">
            <h2 className="text-base font-display font-semibold tracking-tight text-foreground">
              {section.title}
            </h2>
            {section.paragraphs?.map((p, pi) => (
              <Paragraph key={pi} content={p} />
            ))}
            {section.table && (
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground px-4 py-3 border-b border-border">
                        {section.table.header[0]}
                      </th>
                      <th className="text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground px-4 py-3 border-b border-border">
                        {section.table.header[1]}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {section.table.rows.map((row, ri) => (
                      <tr key={ri} className="border-b border-border last:border-b-0">
                        <td className="px-4 py-3 align-top text-muted-foreground">{row[0]}</td>
                        <td className="px-4 py-3 align-top text-foreground">{row[1]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        ))}

        <PublicFooter />
      </main>
    </div>
  );
}
